import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { fetchAdminCommissionSummary, fetchAdminDrivers } from '../services/api/admin';
import type { AdminCommissionSummary, AdminDriverListItem } from '../types/admin';
import { formatCurrency } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

const AdminFinancePage: React.FC = () => {
  const [summary, setSummary] = useState<AdminCommissionSummary | null>(null);
  const [drivers, setDrivers] = useState<AdminDriverListItem[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [nextSummary, driverPage] = await Promise.all([
          fetchAdminCommissionSummary(),
          fetchAdminDrivers({ page, size: pageSize }),
        ]);

        if (!mounted) return;
        setSummary(nextSummary);
        setDrivers(driverPage.items);
        setTotalPages(driverPage.totalPages);
        setTotalElements(driverPage.totalElements);
      } catch (loadError: any) {
        if (!mounted) return;
        setError(loadError.message || 'Could not load the finance summary.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [page, pageSize]);

  return (
    <AdminShell
      badge="Finance Summary"
      copy="Track platform commission performance and scan driver-level earnings visibility for operational support."
      title="Admin finance"
    >
      {error ? <div className="register-inline-error">{error}</div> : null}

      {loading ? (
        <div className="admin-panel">
          <div className="register-loader" />
        </div>
      ) : (
        <>
          <div className="admin-grid admin-grid-cards">
            <div className="admin-panel admin-stat-card">
              <span className="admin-stat-label">Commission today</span>
              <strong>{formatCurrency(summary?.commissionToday)}</strong>
              <span className="admin-stat-note">Current-day platform commission from completed trips.</span>
            </div>
            <div className="admin-panel admin-stat-card">
              <span className="admin-stat-label">Commission this week</span>
              <strong>{formatCurrency(summary?.commissionThisWeek)}</strong>
              <span className="admin-stat-note">Seven-day commission view for quick operational checks.</span>
            </div>
            <div className="admin-panel admin-stat-card">
              <span className="admin-stat-label">Commission this month</span>
              <strong>{formatCurrency(summary?.commissionThisMonth)}</strong>
              <span className="admin-stat-note">Month-to-date commission across completed trips.</span>
            </div>
            <div className="admin-panel admin-stat-card">
              <span className="admin-stat-label">Completed trips</span>
              <strong>{summary?.completedTripsCount ?? 0}</strong>
              <span className="admin-stat-note">Trips currently represented in the finance summary.</span>
            </div>
          </div>

          <section className="admin-panel" style={{ marginTop: '18px' }}>
            <div className="admin-panel-header">
              <div>
                <h2 className="admin-panel-title">Payment operations</h2>
                <p className="admin-panel-copy">
                  Open the payment operations console to search payment intents, provider references, webhooks, payouts, ledger entries, and admin action audits.
                </p>
              </div>
              <div className="admin-inline-actions">
                <Link className="register-secondary-button" to="/admin/payment-ops">
                  Open Payment Ops
                </Link>
                <Link className="admin-payment-action-button" to="/admin/payment-ops?lookup=provider-events">
                  Provider lookup
                </Link>
                <Link className="admin-payment-action-button" to="/admin/payment-ops?lookup=webhook-events">
                  Webhook lookup
                </Link>
              </div>
            </div>
          </section>

          <section className="admin-panel" style={{ marginTop: '18px' }}>
            <div className="admin-panel-header">
              <div>
                <h2 className="admin-panel-title">Driver earnings visibility</h2>
                <p className="admin-panel-copy">Use this list to spot high-level payout/support outliers before opening a driver review detail.</p>
              </div>
            </div>
            <div className="admin-table">
              <div className="admin-table-head">
                <span>Driver</span>
                <span>Status</span>
                <span>Today</span>
                <span>This week</span>
                <span>This month</span>
                <span>Total</span>
              </div>
              {drivers.map((driver) => (
                <div className="admin-table-row" key={driver.driverId}>
                  <span>
                    <strong>{driver.name}</strong>
                    <small>{driver.email}</small>
                    <small>{driver.accountType || 'Account type pending'}</small>
                  </span>
                  <span>
                    <strong>{driver.onboardingReviewStatus || 'No review state'}</strong>
                    <small>{driver.enabled ? 'Enabled' : 'Disabled'}</small>
                  </span>
                  <span><strong>{formatCurrency(driver.dailyEarnings)}</strong></span>
                  <span><strong>{formatCurrency(driver.weeklyEarnings)}</strong></span>
                  <span><strong>{formatCurrency(driver.monthlyEarnings)}</strong></span>
                  <span><strong>{formatCurrency(driver.totalEarnings)}</strong></span>
                </div>
              ))}
              {!drivers.length ? <p className="admin-empty">No driver earnings are available yet.</p> : null}
            </div>
            {drivers.length ? (
              <div className="admin-pagination">
                <span className="admin-muted-note">
                  Page {page + 1} of {Math.max(totalPages, 1)} · {totalElements} drivers
                </span>
                <button
                  className="register-secondary-button"
                  disabled={loading || page <= 0}
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                  type="button"
                >
                  Previous
                </button>
                <button
                  className="register-secondary-button"
                  disabled={loading || page + 1 >= totalPages}
                  onClick={() => setPage((current) => current + 1)}
                  type="button"
                >
                  Next
                </button>
              </div>
            ) : null}
            <p className="admin-muted-note" style={{ marginTop: '16px' }}>
              This is the MVP finance surface. The driver earnings table is now backed by the paginated admin driver list so it can scale as the fleet grows. Payout disputes, early cashout operations, and settlement tooling can extend from here once the underlying backend roles split beyond general admin.
            </p>
          </section>
        </>
      )}
    </AdminShell>
  );
};

export default AdminFinancePage;
